import { useState, useEffect } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Button, Input, Label } from '@pantry/ui';
import { getPasswordChecks, getPasswordStrength, isPasswordValid } from '@pantry/shared';
import { useAuth } from '../../contexts/useAuth';
import {
  EyeIcon,
  EyeSlashIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

export const Route = createFileRoute('/_auth/reset-password')({
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { t } = useLingui();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValidSession, setIsValidSession] = useState<boolean | null>(null);
  const { updatePassword, session } = useAuth();
  const navigate = useNavigate();

  // Check if we have a valid recovery session with retry logic
  useEffect(() => {
    // Supabase automatically detects the recovery token from URL hash
    // and creates a session with type 'recovery'
    // We use retry logic instead of fixed delay for reliability
    let retryCount = 0;
    const maxRetries = 10;
    const baseDelay = 100; // Start with 100ms, exponential backoff
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let isMounted = true;

    const checkSession = () => {
      if (!isMounted) return;

      if (session) {
        setIsValidSession(true);
        return;
      }

      // If we've exceeded retries, give up
      if (retryCount >= maxRetries) {
        setIsValidSession(false);
        return;
      }

      // Exponential backoff: 100ms, 200ms, 400ms, etc.
      const delay = Math.min(baseDelay * Math.pow(2, retryCount), 2000);
      retryCount++;

      timeoutId = setTimeout(checkSession, delay);
    };

    checkSession();

    // Cleanup to prevent memory leaks and state updates on unmounted component
    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [session]);

  // Password validation using shared schema
  const passwordChecks = getPasswordChecks(password);
  const passwordStrength = getPasswordStrength(password);
  const allChecksPassed = isPasswordValid(password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate password using shared schema
    if (!allChecksPassed) {
      setError(t`Password does not meet all requirements`);
      return;
    }

    // Check passwords match
    if (password !== confirmPassword) {
      setError(t`Passwords do not match`);
      return;
    }

    setIsLoading(true);

    try {
      await updatePassword(password);
      setIsSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t`Failed to reset password`);
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state while checking session
  if (isValidSession === null) {
    return (
      <div className="space-y-8">
        <div className="flex flex-col items-center justify-center py-12">
          <svg className="animate-spin h-8 w-8 text-primary" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="mt-4 text-muted-foreground">
            <Trans>Verifying reset link...</Trans>
          </p>
        </div>
      </div>
    );
  }

  // Invalid/expired link
  if (!isValidSession) {
    return (
      <div className="space-y-8">
        <div className="space-y-4 text-center">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-destructive/10 flex items-center justify-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-destructive" />
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">
            <Trans>Invalid or expired link</Trans>
          </h1>
          <p className="text-muted-foreground">
            <Trans>
              This password reset link is invalid or has expired. Please request a new one.
            </Trans>
          </p>
        </div>

        <div className="space-y-3">
          <Link to="/forgot-password">
            <Button className="w-full h-11 font-medium">
              <Trans>Request new reset link</Trans>
            </Button>
          </Link>
          <Link
            to="/login"
            className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            <Trans>Back to sign in</Trans>
          </Link>
        </div>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="space-y-8">
        <div className="space-y-4 text-center">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center">
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">
            <Trans>Password reset successful</Trans>
          </h1>
          <p className="text-muted-foreground">
            <Trans>
              Your password has been updated. You can now sign in with your new password.
            </Trans>
          </p>
        </div>

        <Button className="w-full h-11 font-medium" onClick={() => navigate({ to: '/dashboard' })}>
          <Trans>Continue to dashboard</Trans>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="font-display text-3xl font-semibold tracking-tight">
          <Trans>Set new password</Trans>
        </h1>
        <p className="text-muted-foreground">
          <Trans>
            Create a strong password with at least 8 characters, including uppercase, lowercase, and
            a number.
          </Trans>
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive animate-in">
            <div className="flex items-center gap-2">
              <svg
                className="h-4 w-4 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {error}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="password" className="text-sm font-medium">
            <Trans>New password</Trans>
          </Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder={t`Enter your new password`}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              autoFocus
              className="h-11 pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              {showPassword ? (
                <EyeSlashIcon className="h-5 w-5" />
              ) : (
                <EyeIcon className="h-5 w-5" />
              )}
            </button>
          </div>

          {/* Password strength indicator */}
          {password && (
            <div className="space-y-2 pt-1">
              <div className="flex gap-1">
                {[1, 2, 3].map((level) => (
                  <div
                    key={level}
                    className={`h-1 flex-1 rounded-full transition-colors ${
                      passwordStrength.strength >= level
                        ? passwordStrength.strength === 1
                          ? 'bg-destructive'
                          : passwordStrength.strength === 2
                            ? 'bg-expiring-soon'
                            : 'bg-fresh'
                        : 'bg-muted'
                    }`}
                  />
                ))}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                <span
                  className={`flex items-center gap-1 ${passwordChecks.length ? 'text-fresh' : 'text-muted-foreground'}`}
                >
                  <CheckCircleIcon
                    className={`h-3.5 w-3.5 ${passwordChecks.length ? '' : 'opacity-40'}`}
                  />
                  <Trans>8+ characters</Trans>
                </span>
                <span
                  className={`flex items-center gap-1 ${passwordChecks.uppercase ? 'text-fresh' : 'text-muted-foreground'}`}
                >
                  <CheckCircleIcon
                    className={`h-3.5 w-3.5 ${passwordChecks.uppercase ? '' : 'opacity-40'}`}
                  />
                  <Trans>Uppercase</Trans>
                </span>
                <span
                  className={`flex items-center gap-1 ${passwordChecks.lowercase ? 'text-fresh' : 'text-muted-foreground'}`}
                >
                  <CheckCircleIcon
                    className={`h-3.5 w-3.5 ${passwordChecks.lowercase ? '' : 'opacity-40'}`}
                  />
                  <Trans>Lowercase</Trans>
                </span>
                <span
                  className={`flex items-center gap-1 ${passwordChecks.number ? 'text-fresh' : 'text-muted-foreground'}`}
                >
                  <CheckCircleIcon
                    className={`h-3.5 w-3.5 ${passwordChecks.number ? '' : 'opacity-40'}`}
                  />
                  <Trans>Number</Trans>
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword" className="text-sm font-medium">
            <Trans>Confirm new password</Trans>
          </Label>
          <div className="relative">
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder={t`Confirm your new password`}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              className="h-11 pr-10"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              {showConfirmPassword ? (
                <EyeSlashIcon className="h-5 w-5" />
              ) : (
                <EyeIcon className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        <Button type="submit" className="w-full h-11 font-medium" disabled={isLoading}>
          {isLoading ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <Trans>Resetting password...</Trans>
            </span>
          ) : (
            <Trans>Reset password</Trans>
          )}
        </Button>
      </form>
    </div>
  );
}
